import React, { useState, useEffect } from 'react';
import MainLayout from '@/components/layout/MainLayout';
import { Button } from '@/components/ui/button';
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardFooter, 
  CardHeader, 
  CardTitle 
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Separator } from '@/components/ui/separator';
import { 
  Bot, 
  Save, 
  Settings, 
  BookOpen, 
  UploadCloud, 
  Check, 
  Plus,
  ChevronsUpDown,
  X
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { useToast } from "@/components/ui/use-toast";
import { Progress } from '@/components/ui/progress';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useSupabaseStore } from '@/store/SupabaseStore';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';

const CustomAgent = () => {
  const { toast } = useToast();
  const { user } = useAuth();
  const { addAgent } = useSupabaseStore();
  const [agentInfo, setAgentInfo] = useState({
    name: '',
    description: '',
    instructions: '',
    domain: 'general',
    knowledge: '',
    files: [],
    blueprintIds: [] as string[],
  });
  const [activeTab, setActiveTab] = useState('info');
  const [isCreating, setIsCreating] = useState(false);
  const [progress, setProgress] = useState(0);

  // --- NEW: Fetch user's blueprints from Supabase ---
  const [blueprints, setBlueprints] = useState<{ id: string; name: string }[]>([]);
  useEffect(() => {
    const fetchBlueprints = async () => {
      if (!user) return;
      const { data, error } = await supabase
        .from('blueprints')
        .select('id, name')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });
      if (!error && data) {
        setBlueprints(data);
      }
    };
    fetchBlueprints();
  }, [user]);
  // ---------------------------------------------------

  const handleChange = (e) => {
    const { name, value } = e.target;
    setAgentInfo(prev => ({ ...prev, [name]: value }));
  };

  const handleBlueprintSelect = (blueprintId: string) => {
    setAgentInfo(prev => {
      const newBlueprintIds = prev.blueprintIds.includes(blueprintId)
        ? prev.blueprintIds.filter(id => id !== blueprintId)
        : [...prev.blueprintIds, blueprintId];
      return { ...prev, blueprintIds: newBlueprintIds };
    });
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    setAgentInfo(prev => ({ ...prev, files: [...prev.files, ...files] }));
  };

  const handleRemoveFile = (index) => {
    const updatedFiles = [...agentInfo.files];
    updatedFiles.splice(index, 1);
    setAgentInfo(prev => ({ ...prev, files: updatedFiles }));
  };

  const isFormComplete = () => {
    return agentInfo.name && agentInfo.description && agentInfo.instructions;
  };

  const handleSaveAgent = async () => { // Made async
    if (!isFormComplete()) {
      toast({
        title: "Missing information",
        description: "Please fill in all required fields.",
        variant: "destructive"
      });
      return;
    }
    
    setIsCreating(true);
    setProgress(0); // Reset progress

    try {
      // Simulate progress for a better UX, even if DB operation is fast
      let currentProgress = 0;
      const interval = setInterval(() => {
        currentProgress = Math.min(currentProgress + 10, 90); // Cap at 90% before actual save
        setProgress(currentProgress);
      }, 100);

      const newAgent = await addAgent({
        name: agentInfo.name,
        description: agentInfo.description,
        instructions: agentInfo.instructions,
        domain: agentInfo.domain,
        rating: 0,
        uses: 0,
        is_standard: false,
        icon: 'User',
      }, agentInfo.blueprintIds);

      clearInterval(interval); // Clear interval once DB operation is done
      setProgress(100); // Set to 100% immediately after save

      if (newAgent) {
        // Clear form after successful creation
        setAgentInfo({
          name: '',
          description: '',
          instructions: '',
          domain: 'general',
          knowledge: '',
          files: [],
          blueprintIds: [],
        });
      } else {
        // Error toast is handled by addAgent in SupabaseStore
      }
    } catch (error) {
      console.error("Error creating agent:", error);
      toast({
        title: "Error",
        description: "An unexpected error occurred while creating the agent.",
        variant: "destructive"
      });
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <MainLayout>
      <div className="container px-4 py-6 max-w-6xl mx-auto">
        <div className="flex flex-col space-y-6">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold text-foreground">
                Create Custom AI Agent
              </h1>
              <p className="text-muted-foreground mt-1">
                Design an AI agent tailored to your specific needs
              </p>
            </div>
            
            <Button
              onClick={handleSaveAgent}
              disabled={!isFormComplete() || isCreating}
              className="gap-2 bg-primary hover:bg-primary/90 text-primary-foreground"
            >

              {isCreating ? (
                <>Processing...</>
              ) : (
                <>
                  <Save size={18} />
                  Create Agent
                </>
              )}
            </Button>
          </div>
          
          {isCreating && (
            <Card className="mb-4">
              <CardContent className="pt-6">
                <h3 className="font-medium mb-2">Creating your AI agent...</h3>
                <Progress value={progress} className="h-2" />
                <p className="text-xs text-muted-foreground mt-2">
                  Training and configuring your custom agent. This may take a moment.
                </p>
              </CardContent>
            </Card>
          )}
          
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2">
              <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                <TabsList className="mb-4 grid grid-cols-3">
                  <TabsTrigger value="info" className="gap-2">
                    <Bot size={16} />
                    Basic Info
                  </TabsTrigger>
                  <TabsTrigger value="instructions" className="gap-2">
                    <BookOpen size={16} />
                    Instructions
                  </TabsTrigger>
                  <TabsTrigger value="knowledge" className="gap-2">
                    <UploadCloud size={16} />
                    Knowledge
                  </TabsTrigger>
                </TabsList>
                
                <TabsContent value="info" className="space-y-4">
                  <Card>
                    <CardHeader>
                      <CardTitle>Agent Information</CardTitle>
                      <CardDescription>
                        Define your agent's identity and purpose
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="name">Agent Name <span className="text-red-500">*</span></Label>
                        <Input 
                          id="name" 
                          name="name" 
                          value={agentInfo.name} 
                          onChange={handleChange} 
                          placeholder="e.g., Financial Advisor Bot"
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <Label htmlFor="description">Description <span className="text-red-500">*</span></Label>
                        <Textarea 
                          id="description" 
                          name="description" 
                          value={agentInfo.description} 
                          onChange={handleChange} 
                          placeholder="Describe what your agent does and its expertise"
                          rows={3}
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <Label htmlFor="domain">Domain Expertise</Label>
                        <div className="flex flex-wrap gap-2">
                          {['general', 'finance', 'healthcare', 'technology', 'education', 'legal', 'marketing', 'science', 'engineering'].map(domain => (
                            <Badge
                              key={domain}
                              variant={agentInfo.domain === domain ? "default" : "outline"}
                              className={`cursor-pointer ${agentInfo.domain === domain ? 'bg-primary hover:bg-primary/90 text-primary-foreground' : ''}`}
                              onClick={() => setAgentInfo(prev => ({ ...prev, domain }))}
                            >

                              {domain.charAt(0).toUpperCase() + domain.slice(1)}
                            </Badge>
                          ))}
                        </div>
                      </div>
                      {/* NEW: Blueprint selection */}
                      <div className="space-y-2">
                        <Label htmlFor="blueprintIds">Blueprint Flows</Label>
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button
                              variant="outline"
                              role="combobox"
                              className="w-full justify-between"
                            >
                              {agentInfo.blueprintIds.length > 0
                                ? `${agentInfo.blueprintIds.length} blueprint(s) selected`
                                : "Select blueprints..."}
                              <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-[--radix-popover-trigger-width] p-0">
                            <Command>
                              <CommandInput placeholder="Search blueprints..." />
                              <CommandList>
                                <CommandEmpty>No blueprints found.</CommandEmpty>
                                <CommandGroup>
                                  {blueprints.map((bp) => (
                                    <CommandItem
                                      key={bp.id}
                                      value={bp.name}
                                      onSelect={() => handleBlueprintSelect(bp.id)}
                                    >
                                      <Check
                                        className={`mr-2 h-4 w-4 ${
                                          agentInfo.blueprintIds.includes(bp.id)
                                            ? "opacity-100"
                                            : "opacity-0"
                                        }`}
                                      />
                                      {bp.name}
                                    </CommandItem>
                                  ))}
                                </CommandGroup>
                              </CommandList>
                            </Command>
                          </PopoverContent>
                        </Popover>
                        {agentInfo.blueprintIds.length > 0 && (
                          <div className="flex flex-wrap gap-1 pt-2">
                            {agentInfo.blueprintIds.map(id => {
                              const bp = blueprints.find(b => b.id === id);
                              return bp ? (
                                <Badge key={id} variant="secondary" className="gap-1">
                                  {bp.name}
                                  <button onClick={() => handleBlueprintSelect(id)} className="ml-1 rounded-full hover:bg-muted-foreground/20">
                                    <X className="h-3 w-3" />
                                  </button>
                                </Badge>
                              ) : null;
                            })}
                          </div>
                        )}
                        <p className="text-xs text-muted-foreground">
                          Select one or more saved Blueprint AI Flows to define your agent's logic.
                        </p>
                      </div>
                    </CardContent>
                    <CardFooter className="border-t pt-4">
                      <Button
                        variant="default"
                        onClick={() => setActiveTab('instructions')}
                        className="ml-auto"
                      >
                        Continue to Instructions
                      </Button>
                    </CardFooter>
                  </Card>
                </TabsContent>
                
                <TabsContent value="instructions" className="space-y-4">
                  <Card>
                    <CardHeader>
                      <CardTitle>Agent Instructions</CardTitle>
                      <CardDescription>
                        Provide detailed instructions for your agent's behavior and responses
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="instructions">Instructions <span className="text-red-500">*</span></Label>
                        <Textarea 
                          id="instructions" 
                          name="instructions" 
                          value={agentInfo.instructions} 
                          onChange={handleChange} 
                          placeholder="Be specific about how your agent should respond, what knowledge to draw on, tone, format, etc."
                          rows={8}
                        />
                        <p className="text-xs text-muted-foreground">
                          The more specific your instructions, the better your agent will perform. Include
                          guidance on tone, formatting, response style, and any limitations.
                        </p>
                      </div>
                    </CardContent>
                    <CardFooter className="border-t pt-4 flex justify-between">
                      <Button
                        variant="outline"
                        onClick={() => setActiveTab('info')}
                      >
                        Back to Info
                      </Button>
                      <Button
                        variant="default"
                        onClick={() => setActiveTab('knowledge')}
                      >
                        Continue to Knowledge
                      </Button>
                    </CardFooter>
                  </Card>
                </TabsContent>
                
                <TabsContent value="knowledge" className="space-y-4">
                  <Card>
                    <CardHeader>
                      <CardTitle>Knowledge Base</CardTitle>
                      <CardDescription>
                        Add documents and data to enhance your agent's knowledge
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="knowledge">Additional Knowledge (Optional)</Label>
                        <Textarea 
                          id="knowledge" 
                          name="knowledge" 
                          value={agentInfo.knowledge} 
                          onChange={handleChange} 
                          placeholder="Paste relevant text knowledge your agent should have access to"
                          rows={4}
                        />
                      </div>
                      
                      <Separator className="my-4" />
                      
                      <div className="space-y-2">
                        <Label>Upload Documents (Optional)</Label>
                        <div className="border-2 border-dashed rounded-md p-6 text-center">
                          <input
                            type="file"
                            id="file-upload"
                            className="hidden"
                            multiple
                            onChange={handleFileChange}
                          />
                          <label
                            htmlFor="file-upload"
                            className="cursor-pointer flex flex-col items-center justify-center gap-2"
                          >
                            <UploadCloud className="h-8 w-8 text-muted-foreground" />
                            <span className="text-sm font-medium">
                              Click to upload or drag and drop
                            </span>
                            <span className="text-xs text-muted-foreground">
                              PDF, DOCX, TXT, CSV (max 10MB each)
                            </span>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              className="mt-2"
                            >
                              <Plus size={16} className="mr-2" /> Select Files
                            </Button>
                          </label>
                        </div>
                        
                        {agentInfo.files.length > 0 && (
                          <div className="mt-4">
                            <h4 className="text-sm font-medium mb-2">Uploaded Files ({agentInfo.files.length})</h4>
                            <ul className="space-y-2">
                              {agentInfo.files.map((file, index) => (
                                <li key={index} className="flex items-center justify-between p-2 rounded-md bg-secondary">
                                  <div className="flex items-center">
                                    <Check size={16} className="text-green-500 mr-2" />
                                    <span className="text-sm">{file.name}</span>
                                  </div>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleRemoveFile(index)}
                                    className="h-6 w-6 p-0 text-muted-foreground"
                                  >
                                    ✕
                                  </Button>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    </CardContent>
                    <CardFooter className="border-t pt-4 flex justify-between">
                      <Button
                        variant="outline"
                        onClick={() => setActiveTab('instructions')}
                      >
                        Back to Instructions
                      </Button>
                      <Button
                        variant="default"
                        className="bg-primary hover:bg-primary/90 text-primary-foreground"
                        onClick={handleSaveAgent}
                        disabled={!isFormComplete() || isCreating}
                      >

                        {isCreating ? "Processing..." : "Create Agent"}
                      </Button>
                    </CardFooter>
                  </Card>
                </TabsContent>
              </Tabs>
            </div>
            
            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>Agent Preview</CardTitle>
                  <CardDescription>
                    How your agent will appear to users
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="border rounded-lg p-4">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center text-primary">
                        <Bot size={24} />
                      </div>
                      <div>
                        <h3 className="font-medium">
                          {agentInfo.name || "Your Agent Name"}
                        </h3>
                        <p className="text-xs text-muted-foreground">
                          {agentInfo.domain.charAt(0).toUpperCase() + agentInfo.domain.slice(1)} Expert
                        </p>
                      </div>
                    </div>
                    <p className="text-sm line-clamp-3">
                      {agentInfo.description || "Your agent description will appear here. Provide a clear and concise description of what your agent does."}
                    </p>
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader>
                  <CardTitle>Tips for Great Agents</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <h4 className="text-sm font-medium flex items-center gap-2">
                      <span className="h-5 w-5 rounded-full bg-primary/10 flex items-center justify-center text-primary text-xs">1</span>

                      Be specific with instructions
                    </h4>
                    <p className="text-xs text-muted-foreground">
                      Clear, detailed instructions lead to better results. Specify tone, format, and boundaries.
                    </p>
                  </div>
                  
                  <div className="space-y-2">
                    <h4 className="text-sm font-medium flex items-center gap-2">
                      <span className="h-5 w-5 rounded-full bg-primary/10 flex items-center justify-center text-primary text-xs">2</span>

                      Add relevant knowledge
                    </h4>
                    <p className="text-xs text-muted-foreground">
                      Upload documents that contain domain-specific information your agent needs to be effective.
                    </p>
                  </div>
                  
                  <div className="space-y-2">
                    <h4 className="text-sm font-medium flex items-center gap-2">
                      <span className="h-5 w-5 rounded-full bg-primary/10 flex items-center justify-center text-primary text-xs">3</span>

                      Test and refine
                    </h4>
                    <p className="text-xs text-muted-foreground">
                      After creation, test your agent with different questions and refine its instructions as needed.
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default CustomAgent;